import { useCallback, useMemo } from 'react'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import useAssistantThoughtFocus from 'tripPlanner/components/Chat/useAssistantThoughtFocus'
import AssistantThoughts from 'tripPlanner/components/Chat/AssistantThoughts'
import { canCollapseAssistantThoughts, isInterimAssistantText } from 'tripPlanner/components/Chat/assistantThoughtPolicy'
import PillsList from 'tripPlanner/components/Chat/PillsList'
import { isStreamedPillsListUiComponentData } from 'tripPlanner/utils/streamedProgressiveUi'
import DrawerAskAIText from './DrawerAskAIText'

interface Props {
  message: MessageItem
  onSendMessage?: (message: string) => void
}

function DrawerAskAIResponse({ message, onSendMessage = noop }: Props) {
  const canCollapse = canCollapseAssistantThoughts(message)
  const { toggleRef, onFocusCapture, onBlurCapture } = useAssistantThoughtFocus(canCollapse)
  const thoughts = useMemo(() => message.parts.filter(isInterimAssistantText), [message.parts])
  const firstThoughtIndex = message.parts.findIndex(isInterimAssistantText)
  const handlePillSelection = useCallback((selection: Array<string>) => {
    const label = selection[0]
    if (label) onSendMessage(label)
  }, [onSendMessage])

  return (
    <Group direction="vertical" gap={12} onFocusCapture={onFocusCapture} onBlurCapture={onBlurCapture}>
      {message.parts.map((part, index) => {
        const key = `${message.id}-part-${index}`
        if (canCollapse && isInterimAssistantText(part)) {
          if (index !== firstThoughtIndex) return null
          return (
            <AssistantThoughts
              toggleRef={toggleRef}
              key={`thoughts-${message.id}-${message.generationStartedAt ?? ''}`}
              generationStartedAt={message.generationStartedAt}
              generationFinishedAt={message.generationFinishedAt}
            >
              {thoughts.map((thought, thoughtIndex) => (
                <DrawerAskAIText key={`${message.id}-thought-${thoughtIndex}`} content={thought.content} />
              ))}
            </AssistantThoughts>
          )
        }
        if (part.type === 'text') {
          return <DrawerAskAIText key={key} content={part.content} isInterim={isInterimAssistantText(part)} />
        }
        if (part.type === 'ui_component' && part.data.type === 'pills_list') {
          return (
            <PillsList
              key={key}
              options={part.data.options}
              select={part.data.select}
              onSelectionChange={handlePillSelection}
              isStreaming={isStreamedPillsListUiComponentData(part.data) && part.data.streamingStatus !== 'complete'}
            />
          )
        }
        return null
      })}
    </Group>
  )
}

export default DrawerAskAIResponse
